import React, { useState } from 'react';

import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const AccountInformation = ({ user, onUpdateProfile, onUpdatePreferences }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.address || '',
    city: user?.city || '',
    state: user?.state || '',
    zipCode: user?.zipCode || ''
  });
  const [preferences, setPreferences] = useState({
    emailNotifications: user?.preferences?.emailNotifications ?? true,
    smsNotifications: user?.preferences?.smsNotifications ?? false,
    pushNotifications: user?.preferences?.pushNotifications ?? true,
    marketingEmails: user?.preferences?.marketingEmails ?? false,
    serviceReminders: user?.preferences?.serviceReminders ?? true,
    promotionalOffers: user?.preferences?.promotionalOffers ?? false
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePreferenceChange = (field, value) => {
    setPreferences(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveProfile = async () => {
    setIsLoading(true);
    try {
      await onUpdateProfile(formData);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePreferences = async () => {
    setIsLoading(true);
    try {
      await onUpdatePreferences(preferences);
    } catch (error) {
      console.error('Error updating preferences:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      address: user?.address || '',
      city: user?.city || '',
      state: user?.state || '',
      zipCode: user?.zipCode || ''
    });
    setIsEditing(false);
  };

  return (
    <div className="bg-white rounded-lg shadow-elevation-2 border border-border">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-text-primary">
            Account Information
          </h2>
          {!isEditing && (
            <Button
              variant="outline"
              size="sm"
              iconName="Edit"
              iconPosition="left"
              onClick={() => setIsEditing(true)}
            >
              Edit Profile
            </Button>
          )}
        </div>
      </div>
      <div className="p-6 space-y-6">
        {/* Profile Information */}
        <div>
          <h3 className="text-lg font-medium text-text-primary mb-4">
            Profile Information
          </h3>
          
          {isEditing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Full Name"
                  type="text"
                  value={formData?.name}
                  onChange={(e) => handleInputChange('name', e?.target?.value)}
                  required
                />
                <Input
                  label="Email Address"
                  type="email"
                  value={formData?.email}
                  onChange={(e) => handleInputChange('email', e?.target?.value)}
                  required
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Phone Number"
                  type="tel"
                  value={formData?.phone}
                  onChange={(e) => handleInputChange('phone', e?.target?.value)}
                />
                <Input
                  label="Address"
                  type="text"
                  value={formData?.address}
                  onChange={(e) => handleInputChange('address', e?.target?.value)}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label="City"
                  type="text"
                  value={formData?.city}
                  onChange={(e) => handleInputChange('city', e?.target?.value)}
                />
                <Input
                  label="State"
                  type="text"
                  value={formData?.state}
                  onChange={(e) => handleInputChange('state', e?.target?.value)}
                />
                <Input
                  label="ZIP Code"
                  type="text"
                  value={formData?.zipCode}
                  onChange={(e) => handleInputChange('zipCode', e?.target?.value)}
                />
              </div>
              
              <div className="flex items-center space-x-3 pt-4">
                <Button
                  variant="default"
                  onClick={handleSaveProfile}
                  loading={isLoading}
                  iconName="Save"
                  iconPosition="left"
                >
                  Save Changes
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-text-secondary">Full Name</label>
                  <div className="text-text-primary">{user?.name || 'Not provided'}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-text-secondary">Email Address</label>
                  <div className="text-text-primary">{user?.email || 'Not provided'}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-text-secondary">Phone Number</label>
                  <div className="text-text-primary">{user?.phone || 'Not provided'}</div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-text-secondary">Address</label>
                  <div className="text-text-primary">
                    {user?.address ? (
                      <>
                        {user?.address}<br />
                        {user?.city}, {user?.state} {user?.zipCode}
                      </>
                    ) : (
                      'Not provided'
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-text-secondary">Member Since</label>
                  <div className="text-text-primary">
                    {new Date(user.memberSince)?.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Notification Preferences */}
        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-medium text-text-primary mb-4">
            Notification Preferences
          </h3>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <h4 className="font-medium text-text-primary">Communication</h4>
                <Checkbox
                  label="Email notifications"
                  description="Receive updates via email"
                  checked={preferences?.emailNotifications}
                  onChange={(e) => handlePreferenceChange('emailNotifications', e?.target?.checked)}
                />
                <Checkbox
                  label="SMS notifications"
                  description="Receive text message updates"
                  checked={preferences?.smsNotifications}
                  onChange={(e) => handlePreferenceChange('smsNotifications', e?.target?.checked)}
                />
                <Checkbox
                  label="Push notifications"
                  description="Receive browser notifications"
                  checked={preferences?.pushNotifications}
                  onChange={(e) => handlePreferenceChange('pushNotifications', e?.target?.checked)}
                />
              </div>
              
              <div className="space-y-3">
                <h4 className="font-medium text-text-primary">Content</h4>
                <Checkbox
                  label="Service reminders"
                  description="Reminders about upcoming services"
                  checked={preferences?.serviceReminders}
                  onChange={(e) => handlePreferenceChange('serviceReminders', e?.target?.checked)}
                />
                <Checkbox
                  label="Marketing emails"
                  description="Promotional content and newsletters"
                  checked={preferences?.marketingEmails}
                  onChange={(e) => handlePreferenceChange('marketingEmails', e?.target?.checked)}
                />
                <Checkbox
                  label="Special offers"
                  description="Discounts and promotional offers"
                  checked={preferences?.promotionalOffers}
                  onChange={(e) => handlePreferenceChange('promotionalOffers', e?.target?.checked)}
                />
              </div>
            </div>
            
            <div className="pt-4">
              <Button
                variant="default"
                onClick={handleSavePreferences}
                loading={isLoading}
                iconName="Save"
                iconPosition="left"
              >
                Save Preferences
              </Button>
            </div>
          </div>
        </div>

        {/* Account Actions */}
        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-medium text-text-primary mb-4">
            Account Actions
          </h3>
          
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              iconName="Key"
              iconPosition="left"
            >
              Change Password
            </Button>
            <Button
              variant="outline"
              iconName="Download"
              iconPosition="left"
            >
              Export Data
            </Button>
            <Button
              variant="outline"
              iconName="Trash2"
              iconPosition="left"
              className="text-error hover:text-error border-error hover:border-error"
            >
              Delete Account
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountInformation;